import * as React from 'react'
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  OrganizationBrandingDto,
  OrganizationDto,
} from '@lib/dto/organization.dto'
import type { UpdateOrganizationInput } from '@lib/input/organization.input'
import { organizationService } from '@/services/OrganizationService'
import { organizationKeys } from '@/hooks/queryKeys'

/**
 * Organization-wide settings.
 *
 * A long `staleTime`: a rate changes a few times a year, and anything that
 * needs it — a quote, a total — should not be refetching it on every mount.
 */
export function useOrganization(): UseQueryResult<OrganizationDto> {
  return useQuery({
    queryKey: organizationKeys.detail(),
    queryFn: () => organizationService.get(),
    staleTime: 5 * 60_000,
  })
}

/**
 * The business name and logo, for screens with no session.
 *
 * Login, invite-accept and the shared proposal page all have to say who this
 * is, and none of them can read the settings. Cached hard and never refetched
 * on focus: a business renames itself approximately never, and this sits on
 * the first screen anyone sees.
 */
export function useOrganizationBranding(): UseQueryResult<OrganizationBrandingDto> {
  const query = useQuery({
    queryKey: organizationKeys.branding(),
    queryFn: () => organizationService.getBranding(),
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  /*
   * The browser tab is the same question as the header — who is this — so it
   * is answered in the same place rather than left as a hardcoded string in
   * index.html that a rename would silently leave behind.
   *
   * Safe to run from every caller: they all write the same value, and the
   * assignment is idempotent.
   */
  const name = query.data?.name
  React.useEffect(() => {
    if (name !== undefined && name !== '') {
      document.title = name
    }
  }, [name])

  return query
}

/** Shared so a name save and a phone save cannot pass each other. */
export const organizationUpdateKey = ['organization', 'update'] as const

/** True while any organization patch is still in flight. */
export function useOrganizationUpdatePending(): boolean {
  return useIsMutating({ mutationKey: organizationUpdateKey }) > 0
}

export function useUpdateOrganization(): UseMutationResult<
  OrganizationDto,
  Error,
  UpdateOrganizationInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: organizationUpdateKey,
    mutationFn: (input: UpdateOrganizationInput) =>
      organizationService.update(input),
    // The response is the record as of this write. A slower reply from an
    // earlier write must not put that older snapshot back over a later one.
    onSuccess: (organization) => {
      queryClient.setQueryData<OrganizationDto>(
        organizationKeys.detail(),
        (current) => {
          if (
            current !== undefined &&
            current.updatedAt > organization.updatedAt
          ) {
            return current
          }
          return organization
        },
      )
      // The public copy carries the name as well, so a rename has to reach it
      // or the app keeps greeting people with the old one.
      void queryClient.invalidateQueries({
        queryKey: organizationKeys.branding(),
      })
    },
  })
}
